import type { PatientChatSession } from '@dev-console/types';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { EmptyState, ErrorState, LoadingState } from '@/components/ui/state-views';
import { getPatientAiChat } from '@/config/api-helpers';
import { formatDate } from '@/utils/format';

function FeedbackBadge({ isHelpful }: { isHelpful: boolean | null }) {
  if (isHelpful == null) return null;
  return isHelpful ? (
    <span className='inline-block rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700'>Liked</span>
  ) : (
    <span className='inline-block rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700'>Disliked</span>
  );
}

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function SessionCard({ session }: { session: PatientChatSession }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className='border-b border-neutral-100 last:border-b-0'>
      <button
        className='hover:bg-neutral-25 flex w-full items-center gap-3 px-4 py-3 text-left'
        onClick={() => setExpanded(!expanded)}
        type='button'
      >
        <svg
          className={`size-3 shrink-0 text-neutral-400 transition-transform ${expanded ? 'rotate-90' : ''}`}
          viewBox='0 0 12 12'
          fill='none'
          stroke='currentColor'
          strokeWidth='2'
        >
          <path d='M4.5 2.5L8 6L4.5 9.5' strokeLinecap='round' strokeLinejoin='round' />
        </svg>
        <div className='min-w-0 flex-1'>
          <div className='flex items-center gap-2'>
            <span className='truncate text-sm font-medium text-neutral-900'>
              {session.title ?? 'Untitled conversation'}
            </span>
            {session.isPrivate && (
              <span className='rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-500'>Private</span>
            )}
            <FeedbackBadge isHelpful={session.providerIsHelpful} />
          </div>
          <div className='flex items-center gap-3 text-xs text-neutral-500'>
            <span>
              {session.lastMessageSentAt ? formatDate(session.lastMessageSentAt) : formatDate(session.createdAt)}
            </span>
            <span>{session.messageCount} messages</span>
            {session.summary && <span className='truncate'>— {session.summary}</span>}
          </div>
        </div>
      </button>

      {expanded && (
        <div className='bg-neutral-25/50 border-t border-neutral-50 px-4 py-3'>
          <div className='space-y-3'>
            {session.messages.map((msg) => (
              <div key={msg.id} className={`flex ${msg.role === 'USER' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${
                    msg.role === 'USER'
                      ? 'bg-blue-50 text-blue-900'
                      : 'bg-white text-neutral-800 shadow-sm ring-1 ring-neutral-100'
                  }`}
                >
                  <div className='mb-1 flex items-center gap-2'>
                    <span className='text-xs font-medium text-neutral-500'>
                      {msg.role === 'USER' ? 'Patient' : 'AI'}
                    </span>
                    <span className='text-xs text-neutral-400'>{formatTime(msg.createdAt)}</span>
                    <FeedbackBadge isHelpful={msg.isHelpful} />
                    {msg.classification && (
                      <span className='rounded-full bg-neutral-100 px-1.5 py-0.5 text-xs text-neutral-500'>
                        {msg.classification}
                      </span>
                    )}
                  </div>
                  <div className='whitespace-pre-wrap break-words'>{msg.content}</div>
                  {msg.attachments.length > 0 && (
                    <div className='mt-1.5 flex flex-wrap gap-1'>
                      {msg.attachments.map((a) => (
                        <span key={a.id} className='rounded bg-neutral-100 px-1.5 py-0.5 text-xs text-neutral-600'>
                          {a.type.replace(/_/g, ' ').toLowerCase()}
                          {a.fileName ? `: ${a.fileName}` : ''}
                        </span>
                      ))}
                    </div>
                  )}
                  {msg.quickReplies.length > 0 && (
                    <div className='mt-1.5 flex flex-wrap gap-1'>
                      {msg.quickReplies.map((qr) => (
                        <span
                          key={qr}
                          className='rounded border border-neutral-200 bg-white px-1.5 py-0.5 text-xs text-neutral-600'
                        >
                          {qr}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function AiChatTab({ patientId }: { patientId: number }) {
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ['patient-ai-chat', patientId],
    queryFn: () => getPatientAiChat(patientId),
  });

  if (error)
    return (
      <ErrorState message={error?.message ?? ''} onRetry={() => refetch()} title='AI chat history is unavailable' />
    );
  if (isLoading) return <LoadingState message='Loading AI chat history' />;
  if (!data || data.length === 0) {
    return <EmptyState message='No AI chat sessions found for this patient.' />;
  }

  const totalMessages = data.reduce((sum, s) => sum + s.messageCount, 0);
  const likedMessages = data.flatMap((s) => s.messages).filter((m) => m.isHelpful === true).length;
  const dislikedMessages = data.flatMap((s) => s.messages).filter((m) => m.isHelpful === false).length;

  return (
    <div>
      <div className='flex items-center gap-4 border-b border-neutral-100 px-4 py-3'>
        <span className='text-sm text-neutral-600'>{data.length} sessions</span>
        <span className='text-sm text-neutral-600'>{totalMessages} messages</span>
        {likedMessages > 0 && <span className='text-sm text-green-600'>{likedMessages} liked</span>}
        {dislikedMessages > 0 && <span className='text-sm text-red-600'>{dislikedMessages} disliked</span>}
      </div>
      {data.map((session) => (
        <SessionCard key={session.id} session={session} />
      ))}
    </div>
  );
}
